import { c as _c } from "react/compiler-runtime";
import { Box, HStack, Progress, Text } from '@chakra-ui/react';
import type { CrmPalette } from '@src/components/sco/Admin/AiCrm/crmLane';
import { CrmCard } from '@src/components/sco/Admin/AiCrm/ui/CrmSurface';
import React, { type ReactNode } from 'react';

/** A number with a caption under it: the stat tiles on every desk page. */
export function StatTile(props) {
  const $ = _c(16);
  const {
    compact,
    footer,
    label,
    sub,
    subTone,
    tone,
    value
  } = props;
  const t0 = compact ? 3 : 4;
  const t1 = tone ? "colorPalette.fg" : "fg";
  const t2 = compact ? "lg" : "2xl";
  let t3;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== tone || $[3] !== value) {
    t3 = <Text color={t1} colorPalette={tone} fontSize={t2} fontWeight="bold" letterSpacing="tight" lineHeight="1.1">{value}</Text>;
    $[0] = t1;
    $[1] = t2;
    $[2] = tone;
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== label) {
    t4 = <Text color="fg.muted" fontSize="xs" mt={1.5}>{label}</Text>;
    $[5] = label;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== sub || $[8] !== subTone) {
    t5 = sub ? <Text color={subTone ? "colorPalette.fg" : "fg.subtle"} colorPalette={subTone} fontSize="xs" fontWeight="semibold" mt={0.5}>{sub}</Text> : null;
    $[7] = sub;
    $[8] = subTone;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== footer || $[11] !== t0 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <CrmCard p={t0}>{t3}{t4}{t5}{footer}</CrmCard>;
    $[10] = footer;
    $[11] = t0;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}

/** One row of a bar chart: a label, a bar, and a number. */
export function BarRow(props) {
  const $ = _c(20);
  const {
    labelWidth: t0,
    label,
    note,
    tone,
    value,
    valueLabel,
    max
  } = props;
  const labelWidth = t0 === undefined ? "150px" : t0;
  let t1;
  if ($[0] !== label || $[1] !== labelWidth) {
    t1 = <Text color="fg" flexShrink={0} fontSize="sm" lineClamp={1} w={labelWidth}>{label}</Text>;
    $[0] = label;
    $[1] = labelWidth;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = typeof label === "string" ? label : undefined;
  const t3 = Math.max(max, 1);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Progress.Track><Progress.Range /></Progress.Track>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== tone || $[7] !== value) {
    t5 = <Progress.Root aria-label={t2} colorPalette={tone} flex="1" max={t3} size="xs" value={value}>{t4}</Progress.Root>;
    $[4] = t2;
    $[5] = t3;
    $[6] = tone;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== valueLabel) {
    t6 = <Text fontSize="sm" fontWeight="bold">{valueLabel}</Text>;
    $[9] = valueLabel;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== note) {
    t7 = note ? <Text color="fg.subtle" fontSize="xs">{note}</Text> : null;
    $[11] = note;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <HStack flexShrink={0} gap={1} justify="flex-end" minW="64px">{t6}{t7}</HStack>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t1 || $[17] !== t5 || $[18] !== t8) {
    t9 = <HStack gap={3}>{t1}{t5}{t8}</HStack>;
    $[16] = t1;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
export function Dot(props) {
  const $ = _c(3);
  const t0 = props.size || "2";
  let t1;
  if ($[0] !== props.tone || $[1] !== t0) {
    t1 = <Box bg="colorPalette.solid" borderRadius="full" boxSize={t0} colorPalette={props.tone} flexShrink={0} />;
    $[0] = props.tone;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}